import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'wouter';
import { CookieSettingsButton } from '@/components/sections/CookieConsentManager';
import { MapPin, ChevronRight, ChevronDown, Truck, Sparkles, ShieldCheck } from 'lucide-react';
import { SiFacebook, SiInstagram, SiPinterest } from 'react-icons/si';
import type { IconType } from 'react-icons';

import type { FooterConfig } from '@/lib/sections/types';
import { FOOTER_CONFIG } from '@/data/footerConfig';
import { isUnavailableBouquetCreatorPath } from '@/lib/storeFeatures';
import { LOCAL_SEO_LOCATIONS, localSeoLocationUrl, type LocalSeoLocation } from '@/data/localSeoLocations';
const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, '');

/* ─── Social icon map — resolves the JSON-serializable iconKey from config ── */
const SOCIAL_ICON_MAP: Record<string, IconType> = {
  facebook: SiFacebook,
  instagram: SiInstagram,
  pinterest: SiPinterest,
};

const AREA_ICON_MAP: Record<string, typeof MapPin> = {
  mapPin: MapPin,
  truck: Truck,
};

const BENEFIT_ICON_MAP: Record<string, typeof Truck> = {
  truck: Truck,
  sparkles: Sparkles,
  shieldCheck: ShieldCheck,
};

function DistrictLink({ location }: { location: LocalSeoLocation }) {
  return (
    <li className="h-[29px] flex items-center">
      <Link
        href={localSeoLocationUrl(location)}
        className="group inline-flex items-center gap-1.5 text-[13.5px] text-[rgba(245,231,235,0.58)] hover:text-[#F5E7EB] transition-colors duration-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d98cb3] rounded"
      >
        <span>{location.name}</span>
        <ChevronRight className="w-3.5 h-3.5 text-[#D884A4] transition-transform duration-200 group-hover:translate-x-[3px]" />
      </Link>
    </li>
  );
}

function AreaTabHeader({
  active, onClick, icon: Icon, title,
}: { active: boolean; onClick: () => void; icon: typeof MapPin; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="group flex items-center gap-3 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d98cb3] rounded-lg transition-all duration-200 w-full px-3 py-2.5"
      style={{
        background: active ? 'rgba(216,132,164,0.10)' : 'transparent',
        border: active ? '1px solid rgba(216,154,178,0.18)' : '1px solid transparent',
      }}
    >
      <Icon className={`w-5 h-5 shrink-0 transition-colors duration-200 ${active ? 'text-[#D884A4]' : 'text-[rgba(216,132,164,0.45)] group-hover:text-[#D884A4]'}`} />
      <span className="relative">
        <span className={`font-serif text-[19px] tracking-wide transition-colors duration-200 ${active ? 'text-[#F5E7EB]' : 'text-[rgba(245,231,235,0.55)] group-hover:text-[rgba(245,231,235,0.85)]'}`}>
          {title}
        </span>
        <span
          className="absolute left-0 -bottom-0.5 h-px bg-[#D884A4] transition-all duration-200"
          style={{ width: active ? '100%' : '0%' }}
          aria-hidden="true"
        />
      </span>
    </button>
  );
}

function DistrictsSection({ config }: { config: FooterConfig['deliveryArea'] }) {
  const [tab, setTab] = useState(config.tabs[0].key);
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeTab = config.tabs.find(item => item.key === tab) ?? config.tabs[0];
  const MobileAreaIcon = AREA_ICON_MAP[config.tabs[0].iconKey];
  const activeLocations = LOCAL_SEO_LOCATIONS.filter((location) =>
    activeTab.key === 'districts' ? location.type === 'district' : location.type === 'nearbyTown',
  );
  const columnSize = Math.ceil(activeLocations.length / 4);
  const cols = Array.from({ length: 4 }, (_, index) => activeLocations.slice(index * columnSize, (index + 1) * columnSize)).filter((column) => column.length > 0);

  return (
    <section
      aria-label={config.ariaLabel}
      className="rounded-[10px]"
      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(216,154,178,0.12)' }}
    >
      {/* Mobile-only toggle header — hidden on md+ */}
      <button
        type="button"
        onClick={() => setMobileOpen(o => !o)}
        className="md:hidden w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d98cb3] rounded-[10px]"
        aria-expanded={mobileOpen}
      >
        <span className="flex items-center gap-2.5 text-[#faeaef]/70 text-[13px] tracking-[0.1em] uppercase font-medium">
          <MobileAreaIcon className="w-4 h-4 text-[#d98cb3]" />
          {config.mobileHeading}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#d98cb3] transition-transform duration-300 ${mobileOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Mobile collapsible body */}
      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden md:hidden"
          >
            <div className="p-5" style={{ borderTop: '1px solid rgba(216,154,178,0.12)' }}>
              <div className="flex flex-col gap-6">
                <div className="flex gap-2 mb-1">
                  {config.tabs.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`flex-1 py-2 rounded-full text-[12px] font-medium transition-all focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d98cb3] ${tab === item.key ? 'bg-[rgba(216,132,164,0.18)] text-[#F5E7EB] border border-[rgba(216,154,178,0.25)]' : 'text-[rgba(245,231,235,0.5)] border border-transparent hover:text-[#F5E7EB]'}`}
                  >
                    {item.mobileTitle}
                  </button>
                  ))}
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.nav
                    key={tab}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    aria-label={activeTab.navigationLabel}
                    className="grid grid-cols-2 gap-x-4 gap-y-1"
                  >
                    {activeLocations.map(location => (
                      <DistrictLink key={location.slug} location={location} />
                    ))}
                  </motion.nav>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop layout — always visible, hidden on mobile */}
      <div className="hidden md:block p-5 md:p-7">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-0">
          {/* Info column with tabs */}
          <div className="lg:w-[290px] lg:shrink-0 lg:pr-8 flex flex-col items-start">
            <div className="mb-3">
              <AreaTabHeader active={tab === config.tabs[0].key} onClick={() => setTab(config.tabs[0].key)} icon={AREA_ICON_MAP[config.tabs[0].iconKey]} title={config.tabs[0].desktopTitle} />
            </div>
            <p className="text-[13.5px] leading-relaxed text-[rgba(245,231,235,0.78)] mb-5">
              {config.tabs[0].desktopDescriptionLines.map((line, index) => <span key={line}>{line}{index < config.tabs[0].desktopDescriptionLines.length - 1 && <br />}</span>)}
            </p>

            {/* Separator */}
            <div className="w-full h-px mb-5" style={{ background: 'rgba(216,154,178,0.13)', opacity: 0.5 }} aria-hidden="true" />

            <div className="mb-3">
              <AreaTabHeader active={tab === config.tabs[1].key} onClick={() => setTab(config.tabs[1].key)} icon={AREA_ICON_MAP[config.tabs[1].iconKey]} title={config.tabs[1].desktopTitle} />
            </div>
            <p className="text-[13.5px] leading-relaxed text-[rgba(245,231,235,0.78)]">
              {config.tabs[1].desktopDescriptionLines.map((line, index) => <span key={line}>{line}{index < config.tabs[1].desktopDescriptionLines.length - 1 && <br />}</span>)}
            </p>
          </div>

          {/* Location columns — animated tab content */}
          <nav
            aria-label={activeTab.navigationLabel}
            className="flex-1 lg:border-l lg:border-[rgba(216,154,178,0.13)] lg:pl-8 lg:my-1 overflow-hidden"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={tab}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2"
              >
                {cols.map((col, i) => (
                  <ul
                    key={i}
                    className={i > 0 ? 'lg:border-l lg:border-[rgba(216,154,178,0.13)] lg:pl-6 lg:my-1' : ''}
                  >
                    {col.map(location => <DistrictLink key={location.slug} location={location} />)}
                  </ul>
                ))}
              </motion.div>
            </AnimatePresence>
          </nav>
        </div>
      </div>
    </section>
  );
}

/* ─── Components ─────────────────────────────────────────────── */


function AccordionColumn({ title, links, isMobile }: { title: string, links: any[], isMobile: boolean }) {
  const [isOpen, setIsOpen] = useState(false);

  if (!isMobile) {
    return (
      <div className="flex flex-col">
        <h3 className="text-[#faeaef] font-serif text-[22px] mb-6 tracking-wide">{title}</h3>
        <ul className="flex flex-col space-y-3">
          {links.map((link) => (
            <li key={link.label}>
              {isUnavailableBouquetCreatorPath(link.href) ? (
                <span aria-disabled="true" className="flex items-center justify-between gap-2 text-[#dcb8c4] text-[15px]">
                  <span>{link.label}</span>
                  <span className="text-[11px] text-[#faeaef]">Wkrótce</span>
                </span>
              ) : <Link href={link.href} className="group flex items-center justify-between text-[#dcb8c4] hover:text-[#faeaef] transition-colors text-[15px]">
                <span>{link.label}</span>
                <ChevronRight className="w-3.5 h-3.5 opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#d98cb3]" />
              </Link>}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="border-b border-[#4a1b2d] py-4">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="flex items-center justify-between w-full text-left text-[#faeaef] font-serif text-[22px] tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d98cb3] rounded"
        aria-expanded={isOpen}
      >
        {title}
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 text-[#d98cb3] ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <ul className="flex flex-col space-y-4 pt-5 pb-2">
              {links.map((link) => (
                <li key={link.label}>
                  {isUnavailableBouquetCreatorPath(link.href) ? (
                    <span aria-disabled="true" className="flex items-center justify-between gap-2 text-[#dcb8c4] text-[15px] py-1">
                      <span>{link.label}</span>
                      <span className="text-[11px] text-[#faeaef]">Wkrótce</span>
                    </span>
                  ) : <Link href={link.href} className="group flex items-center justify-between text-[#dcb8c4] hover:text-[#faeaef] transition-colors text-[15px] py-1">
                    <span>{link.label}</span>
                    <ChevronRight className="w-4 h-4 text-[#d98cb3]" />
                  </Link>}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PaymentLogo({ logoKey, label }: { logoKey: string; label: string }) {
  switch (logoKey) {
    case 'visa':
      return (
        <div className="bg-[#1A1F71] rounded h-[38px] w-[60px] flex items-center justify-center shadow-sm">
          <span className="text-white font-bold text-[14px] tracking-wider italic">{label}</span>
        </div>
      );
    case 'mastercard':
      return (
        <div className="bg-[#252525] rounded h-[38px] w-[60px] flex items-center justify-center shadow-sm">
          <svg width="28" height="18" viewBox="0 0 44 28" fill="none" role="img" aria-label={label}>
            <circle cx="17" cy="14" r="8" fill="#EB001B"/>
            <circle cx="27" cy="14" r="8" fill="#F79E1B"/>
            <path d="M22 7.54A8 8 0 0 1 26.46 14 8 8 0 0 1 22 20.46 8 8 0 0 1 17.54 14 8 8 0 0 1 22 7.54Z" fill="#FF5F00"/>
          </svg>
        </div>
      );
    case 'blik':
      return (
        <div className="bg-black rounded h-[38px] w-[60px] flex items-center justify-center border border-[#333] shadow-sm p-1">
          <img src={`${BASE_URL}/logo-blik.png`} alt={label} width={896} height={424} loading="lazy" className="h-[24px] w-auto object-contain" />
        </div>
      );
    case 'przelewy24':
      return (
        <div className="bg-white rounded h-[38px] px-3 flex items-center justify-center shadow-sm">
          <img src={`${BASE_URL}/logo-p24.png`} alt={label} width={380} height={133} loading="lazy" className="h-[18px] w-auto object-contain" />
        </div>
      );
    default:
      return null;
  }
}

export function Footer({ config = FOOTER_CONFIG, showDeliveryAreas = true }: { config?: FooterConfig; showDeliveryAreas?: boolean } = {}) {
  const { tagline, description, copyright, linkColumns, socialLinks, deliveryArea, benefits, payments, bottomLinks } = config;
  const isMobile = useIsMobile();

  return (
    <footer
      className="relative bg-[#1a050d] font-sans border-t border-[#4a1b2d] mt-auto overflow-hidden"
      style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)' }}
    >

      <div className="page-container relative z-10 pt-8 md:pt-16 lg:pt-24 pb-12 flex flex-col gap-8 md:gap-12 lg:gap-16">
        
        {/* ROW 1: 5 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Col */}
          <div className="lg:col-span-1 border-b lg:border-b-0 lg:border-r border-[#4a1b2d] pb-5 md:pb-8 lg:pb-0 lg:pr-8 flex flex-col">
            {/* Mobile: logo + social icons in a compact row */}
            <div className="flex items-center justify-between lg:block">
              <Link href="/" className="inline-block w-fit focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d98cb3] rounded mb-0 lg:mb-6">
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2.5">
                    <picture>
                      <source srcSet={`${BASE_URL}/logo-256.webp`} type="image/webp" />
                      <img src={`${BASE_URL}/logo.png`} alt="" width={547} height={463} loading="lazy" className="h-[32px] md:h-[40px] w-auto filter brightness-0 invert opacity-[0.95]" />
                    </picture>
                    <span className="font-serif text-[22px] md:text-[26px] leading-none text-[#faeaef] tracking-wide">{config.brandName}</span>
                  </span>
                  <span className="hidden md:flex items-center gap-2 text-[#d98cb3] text-[10px] tracking-[0.3em] uppercase">
                    <span className="h-px w-6 bg-[#d98cb3]/50" aria-hidden="true" />
                    {tagline}
                    <span className="h-px w-6 bg-[#d98cb3]/50" aria-hidden="true" />
                  </span>
                </span>
              </Link>
              {/* Social icons: inline on mobile, below description on desktop */}
              <div className="flex items-center gap-2 lg:hidden">
                {socialLinks.map(s => {
                  const Icon = SOCIAL_ICON_MAP[s.iconKey];
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="w-8 h-8 rounded-full border border-[#4a1b2d] flex items-center justify-center text-[#dcb8c4] hover:text-[#faeaef] hover:border-[#d98cb3] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d98cb3]"
                    >
                      <Icon size={15} aria-hidden="true" focusable="false" />
                    </a>
                  );
                })}
              </div>
            </div>
            <p className="hidden md:block text-[#dcb8c4] text-[15px] leading-relaxed my-6 max-w-[300px]">
              {description}
            </p>
            {/* Social icons: desktop version, below description */}
            <div className="hidden lg:flex items-center gap-3">
              {socialLinks.map(s => {
                  const Icon = SOCIAL_ICON_MAP[s.iconKey];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full border border-[#4a1b2d] flex items-center justify-center text-[#dcb8c4] hover:text-[#faeaef] hover:border-[#d98cb3] hover:-translate-y-1 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d98cb3]"
                  >
                    <Icon size={18} aria-hidden="true" focusable="false" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links Cols */}
          <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-10">
            {linkColumns.map(col => (
              <AccordionColumn key={col.title} title={col.title} links={col.links} isMobile={isMobile} />
            ))}
          </div>
        </div>

        {/* ROW 2: Dzielnice / Okolice */}
        {showDeliveryAreas && (
          <div className="py-6 md:py-10 lg:py-12 border-t border-[#4a1b2d]">
            <DistrictsSection config={deliveryArea} />
          </div>
        )}

        {/* ROW 3: Zalety & Płatności */}
        <div className="pt-6 md:pt-10 lg:pt-12 border-t border-[#4a1b2d] flex flex-col lg:flex-row gap-6 md:gap-10 justify-between items-center">
          
          {/* Benefits */}
          <div className="flex flex-col sm:flex-row w-full lg:w-auto justify-between sm:justify-start items-start sm:items-center gap-8 sm:gap-14">
            {benefits.flatMap((benefit, index) => {
              const Icon = BENEFIT_ICON_MAP[benefit.iconKey];
              return [
                index > 0 && <div key={`${benefit.iconKey}-separator`} className="hidden sm:block w-px h-[50px] bg-[#4a1b2d]"></div>,
                <div key={benefit.iconKey} className="flex items-center gap-4 group cursor-default">
                  <Icon className="w-[34px] h-[34px] text-[#d98cb3] shrink-0 group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-300" />
                  <div>
                    <span className="block text-[#faeaef] text-[16px] tracking-wide mb-0.5">{benefit.title}</span>
                    <span className="block text-[#dcb8c4] text-[14px]">{benefit.description}</span>
                  </div>
                </div>,
              ];
            })}
          </div>

          {/* Payments */}
          <div className="flex flex-wrap items-center justify-start gap-4 lg:gap-5 w-full lg:w-auto" aria-label={payments.ariaLabel}>
            {payments.methods.map(method => <PaymentLogo key={method.logoKey} {...method} />)}
          </div>
        </div>

      </div>

      {/* Bottom bar */}
      <div className="relative z-10 border-t border-[#4a1b2d]">
        <div className="page-container py-5 flex flex-col md:flex-row justify-between items-center gap-5 text-[13px] text-[#b79aa5]">
          <p>{copyright}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
            {bottomLinks.flatMap(link => [
              <Link key={link.href} href={link.href} className="hover:text-[#faeaef] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d98cb3] rounded">{link.label}</Link>,
              <span key={`${link.href}-sep`} className="text-[#4a1b2d] hidden sm:inline">|</span>,
            ])}
            <CookieSettingsButton />
          </div>
        </div>
      </div>
    </footer>
  );
}
